import { useCallback, useEffect, useMemo, useRef, useState, RefObject } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useBoardStore } from '../../stores/boardStore';
import { Column } from './Column';
import { Card } from './Card';
import { Card as CardType, api } from '../../api/client';
import { BoardSkeleton } from '../common/Skeleton';

function useBoardInfoCollapsed(boardId: string | undefined) {
  const key = boardId ? `docanban-info-collapsed-${boardId}` : null;
  const [collapsed, setCollapsed] = useState(() => {
    if (!key) return false;
    return localStorage.getItem(key) === '1';
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      if (key) localStorage.setItem(key, next ? '1' : '0');
      return next;
    });
  }, [key]);

  return [collapsed, toggle] as const;
}

function useActiveColumnIndex(scrollRef: RefObject<HTMLDivElement | null>, columnCount: number) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || columnCount === 0) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const columnWidth = container.scrollWidth / (columnCount + 1);
      const index = Math.round(scrollLeft / columnWidth);
      setActiveIndex(Math.min(index, columnCount - 1));
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [scrollRef, columnCount]);

  return activeIndex;
}

function SwipeIndicator({ count, activeIndex, scrollRef }: {
  count: number;
  activeIndex: number;
  scrollRef: RefObject<HTMLDivElement | null>;
}) {
  if (count <= 1) return null;

  const scrollToColumn = (index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const columnWidth = container.scrollWidth / (count + 1);
    container.scrollTo({ left: columnWidth * index, behavior: 'smooth' });
  };

  return (
    <div className="flex items-center justify-center gap-1.5 py-2 sm:hidden">
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          onClick={() => scrollToColumn(i)}
          className={`rounded-full transition-all duration-200 ${
            i === activeIndex
              ? 'w-6 h-2 bg-blue-500'
              : 'w-2 h-2 bg-slate-300'
          }`}
          aria-label={`Go to column ${i + 1}`}
        />
      ))}
    </div>
  );
}

const COLUMN_COLOR_OPTIONS = [
  '#DBEAFE',
  '#FEF3C7',
  '#D1FAE5',
  '#FCE7F3',
  '#EDE9FE',
  '#FDE68A',
];

export function Board() {
  const {
    board,
    currentUser,
    tags,
    isLoading,
    error,
    initializeBoard,
    syncBoardFromLocation,
    moveCard,
    createColumn,
    createBoard,
    switchBoard,
    refreshBoard,
  } = useBoardStore();

  const [activeCard, setActiveCard] = useState<CardType | null>(null);
  const [filterTagId, setFilterTagId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [isColumnComposerOpen, setIsColumnComposerOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnColor, setNewColumnColor] = useState(COLUMN_COLOR_OPTIONS[0]);
  const [isCreatingColumn, setIsCreatingColumn] = useState(false);
  const [columnError, setColumnError] = useState('');
  const hasInitialized = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    void initializeBoard();
  }, [initializeBoard]);

  useEffect(() => {
    const handlePopState = () => {
      void syncBoardFromLocation();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [syncBoardFromLocation]);

  useEffect(() => {
    const handleTagFilterChange = (e: CustomEvent<string | null>) => {
      setFilterTagId(e.detail);
    };

    window.addEventListener('tagFilterChange', handleTagFilterChange as EventListener);
    return () => {
      window.removeEventListener('tagFilterChange', handleTagFilterChange as EventListener);
    };
  }, []);

  // Keyboard navigation: j/k (card up/down), h/l (column left/right)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (!board || useBoardStore.getState().selectedCardId) return;

      const allCards = board.columns.flatMap((col) =>
        col.cards.map((card) => ({ card, columnId: col.id }))
      );
      if (allCards.length === 0) return;

      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault();
        const focusedEl = document.querySelector('[data-card-focused="true"]');
        const focusedId = focusedEl?.getAttribute('data-card-id');
        const currentIdx = focusedId ? allCards.findIndex((c) => c.card.id === focusedId) : -1;

        let nextIdx: number;
        if (currentIdx === -1) {
          nextIdx = 0;
        } else {
          nextIdx = e.key === 'j'
            ? Math.min(currentIdx + 1, allCards.length - 1)
            : Math.max(currentIdx - 1, 0);
        }

        document.querySelectorAll('[data-card-focused]').forEach((el) => el.removeAttribute('data-card-focused'));
        const nextEl = document.querySelector(`[data-card-id="${allCards[nextIdx].card.id}"]`);
        nextEl?.setAttribute('data-card-focused', 'true');
        nextEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      if (e.key === 'Enter') {
        const focusedEl = document.querySelector('[data-card-focused="true"]');
        const focusedId = focusedEl?.getAttribute('data-card-id');
        if (focusedId) {
          e.preventDefault();
          useBoardStore.getState().selectCard(focusedId);
        }
      }

      if (e.key === 'Escape') {
        document.querySelectorAll('[data-card-focused]').forEach((el) => el.removeAttribute('data-card-focused'));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [board]);

  const filteredBoard = useMemo(() => {
    if (!board || !filterTagId) return board;

    return {
      ...board,
      columns: board.columns.map((column) => ({
        ...column,
        cards: column.cards.filter((card) =>
          card.tags.some((tag) => tag.id === filterTagId)
        ),
      })),
    };
  }, [board, filterTagId]);

  const [infoCollapsed, toggleInfo] = useBoardInfoCollapsed(board?.id);
  const columnCount = filteredBoard?.columns.length || 0;
  const activeColumnIndex = useActiveColumnIndex(scrollContainerRef, columnCount);

  const canManageColumns = currentUser?.id === board?.owner_id;
  const totalCards = filteredBoard?.columns.reduce((sum, column) => sum + column.cards.length, 0) || 0;
  const totalCardsInBoard = board?.columns.reduce((sum, column) => sum + column.cards.length, 0) || 0;
  const doneCards = board?.columns.find((column) => column.name.toLowerCase() === 'done')?.cards.length || 0;
  const completionRate = totalCardsInBoard > 0 ? Math.round((doneCards / totalCardsInBoard) * 100) : 0;

  const clearTagFilter = () => {
    setFilterTagId(null);
    window.dispatchEvent(new CustomEvent('tagFilterChange', { detail: null }));
  };

  const handleDragStart = (event: DragStartEvent) => {
    const activeData = event.active.data.current;

    if (activeData?.type === 'card') {
      setActiveCard(activeData.card);
    }
  };

  const handleDragOver = (_event: DragOverEvent) => {
    // Visual feedback is handled by the column dropzone itself.
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveCard(null);

    if (!over || !board) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (activeData?.type !== 'card') return;

    const draggedCard = activeData.card as CardType;
    let targetColumnId: string;
    let targetPosition: number;

    if (overData?.type === 'column') {
      targetColumnId = over.id as string;
      const targetColumn = board.columns.find((column) => column.id === targetColumnId);
      targetPosition = targetColumn?.cards.length || 0;
    } else if (overData?.type === 'card') {
      const overCard = overData.card as CardType;
      targetColumnId = overCard.column_id;

      const targetColumn = board.columns.find((column) => column.id === targetColumnId);
      if (!targetColumn) return;

      const overIndex = targetColumn.cards.findIndex((card) => card.id === overCard.id);
      targetPosition = overIndex;

      if (draggedCard.column_id === targetColumnId) {
        const activeIndex = targetColumn.cards.findIndex((card) => card.id === draggedCard.id);
        if (activeIndex < overIndex) {
          targetPosition = overIndex;
        }
      }
    } else {
      return;
    }

    if (draggedCard.column_id === targetColumnId && draggedCard.position === targetPosition) {
      return;
    }

    void moveCard(draggedCard.id, targetColumnId, targetPosition);
  };

  const [showBoardForm, setShowBoardForm] = useState(false);
  const [newBoardName, setNewBoardName] = useState('My Documents');
  const [isCreatingBoard, setIsCreatingBoard] = useState(false);

  const handleCreateFirstBoard = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newBoardName.trim()) return;

    setIsCreatingBoard(true);
    try {
      const newBoard = await createBoard(newBoardName.trim());
      await switchBoard(newBoard.id);
    } catch (createError) {
      console.error('Failed to create board:', createError);
    } finally {
      setIsCreatingBoard(false);
    }
  };

  const openColumnComposer = () => {
    const colorIndex = board ? board.columns.length % COLUMN_COLOR_OPTIONS.length : 0;
    setNewColumnColor(COLUMN_COLOR_OPTIONS[colorIndex]);
    setNewColumnName('');
    setColumnError('');
    setIsColumnComposerOpen(true);
  };

  const handleCreateColumn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newColumnName.trim()) {
      setColumnError('Column name is required.');
      return;
    }

    setIsCreatingColumn(true);
    setColumnError('');

    try {
      await createColumn({
        name: newColumnName.trim(),
        color: newColumnColor,
      });
      setIsColumnComposerOpen(false);
      setNewColumnName('');
    } catch (createError) {
      setColumnError((createError as Error).message);
    } finally {
      setIsCreatingColumn(false);
    }
  };

  if (isLoading) {
    return <BoardSkeleton />;
  }

  if (error?.includes('No board found') || !filteredBoard) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl mb-6 shadow-lg">
            <svg className="w-12 h-12 text-blue-600" viewBox="0 0 100 100" fill="currentColor">
              <rect width="100" height="100" rx="12" />
              <rect x="15" y="20" width="20" height="60" rx="4" fill="white" opacity="0.9" />
              <rect x="40" y="30" width="20" height="50" rx="4" fill="white" opacity="0.7" />
              <rect x="65" y="25" width="20" height="55" rx="4" fill="white" opacity="0.8" />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to Docanban!
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Create your first board and Docanban will keep its own URL, so refreshes and shared links land in the right place.
          </p>

          {showBoardForm ? (
            <form onSubmit={handleCreateFirstBoard} className="inline-flex flex-col gap-3 w-full max-w-xs">
              <input
                type="text"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                placeholder="Board name"
                autoFocus
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isCreatingBoard || !newBoardName.trim()}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg disabled:opacity-50 transition-all duration-200"
                >
                  {isCreatingBoard ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBoardForm(false)}
                  className="px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-xl text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowBoardForm(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Your First Board
            </button>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-red-500 mb-2">Error: {error}</div>
          <button
            onClick={() => void initializeBoard()}
            className="text-blue-600 hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      <div className="shrink-0 px-3 pt-3 sm:px-6 sm:pt-4">
        <section className="rounded-2xl border border-white/60 bg-white/80 shadow-[0_8px_24px_rgba(15,76,129,0.06)] backdrop-blur-xl sm:rounded-[28px]">
          {/* Compact summary bar - always visible */}
          <button
            onClick={toggleInfo}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left sm:px-5"
          >
            <div className="flex items-center gap-3 min-w-0">
              <h1 className="truncate text-base font-semibold text-slate-900 sm:text-lg">
                {filteredBoard.name}
              </h1>
              <div className="hidden items-center gap-2 sm:flex">
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">
                  {totalCardsInBoard} docs
                </span>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">
                  {board?.columns.length || 0} cols
                </span>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">
                  {completionRate}% done
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1.5 sm:hidden">
                <span className="text-[11px] font-semibold text-slate-500">{totalCardsInBoard}</span>
                <span className="text-[11px] text-slate-400">docs</span>
              </div>
              <svg
                className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${infoCollapsed ? '' : 'rotate-180'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {/* Expandable detail panel */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              infoCollapsed ? 'max-h-0' : 'max-h-[500px]'
            }`}
          >
            <div className="border-t border-slate-100 px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="max-w-2xl min-w-0">
                  {editingDescription ? (
                    <div className="flex items-start gap-2">
                      <textarea
                        value={descriptionDraft}
                        onChange={(e) => setDescriptionDraft(e.target.value)}
                        placeholder="Add a board description..."
                        maxLength={500}
                        rows={2}
                        autoFocus
                        className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setEditingDescription(false);
                          }
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            const trimmed = descriptionDraft.trim();
                            if (board) {
                              void api.updateBoard(board.id, { description: trimmed || null }).then(() => refreshBoard());
                            }
                            setEditingDescription(false);
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const trimmed = descriptionDraft.trim();
                          if (board) {
                            void api.updateBoard(board.id, { description: trimmed || null }).then(() => refreshBoard());
                          }
                          setEditingDescription(false);
                        }}
                        className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingDescription(false)}
                        className="rounded-lg px-3 py-2 text-xs text-slate-500 hover:bg-slate-100"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setDescriptionDraft(board?.description || '');
                        setEditingDescription(true);
                      }}
                      className="group/desc text-left w-full"
                    >
                      {board?.description ? (
                        <p className="max-w-xl text-sm leading-relaxed text-slate-600 group-hover/desc:text-slate-900 transition-colors">
                          {board.description}
                          <span className="ml-2 text-[10px] text-slate-400 opacity-0 group-hover/desc:opacity-100 transition-opacity">Edit</span>
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400 italic group-hover/desc:text-slate-600 transition-colors">
                          Click to add a board description...
                        </p>
                      )}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-2 sm:gap-3 xl:min-w-[420px]">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 sm:rounded-2xl sm:px-4 sm:py-3">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 sm:text-[11px]">Docs</div>
                    <div className="mt-0.5 text-lg font-semibold text-slate-900 sm:text-2xl">{totalCardsInBoard}</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 sm:rounded-2xl sm:px-4 sm:py-3">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 sm:text-[11px]">Cols</div>
                    <div className="mt-0.5 text-lg font-semibold text-slate-900 sm:text-2xl">{board?.columns.length || 0}</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 sm:rounded-2xl sm:px-4 sm:py-3">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 sm:text-[11px]">Tags</div>
                    <div className="mt-0.5 text-lg font-semibold text-slate-900 sm:text-2xl">{tags.length}</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 sm:rounded-2xl sm:px-4 sm:py-3">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 sm:text-[11px]">Done</div>
                    <div className="mt-0.5 text-lg font-semibold text-slate-900 sm:text-2xl">{completionRate}%</div>
                  </div>
                </div>
              </div>

              {filterTagId && (
                <div className="mt-4 flex flex-col gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:rounded-2xl sm:px-4 sm:py-3">
                  <div>
                    <div className="text-sm font-semibold text-blue-800">Filtered view</div>
                    <p className="mt-0.5 text-xs text-blue-700 sm:text-sm">
                      Showing {totalCards} of {totalCardsInBoard} documents.
                    </p>
                  </div>
                  <button
                    onClick={clearTagFilter}
                    className="inline-flex items-center justify-center rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-blue-700 shadow-sm hover:bg-blue-100 sm:rounded-xl sm:py-2 sm:text-sm"
                  >
                    Clear filter
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Collapsed filter indicator */}
          {filterTagId && infoCollapsed && (
            <div className="border-t border-blue-100 bg-blue-50/60 px-4 py-2 flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-blue-700">
                Filtered: {totalCards} of {totalCardsInBoard} docs
              </span>
              <button
                onClick={clearTagFilter}
                className="text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Clear
              </button>
            </div>
          )}
        </section>
      </div>

      <div className="relative mt-3 min-h-0 flex-1 sm:mt-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div ref={scrollContainerRef} className="flex h-full items-start gap-4 overflow-x-auto px-3 pb-6 snap-x snap-mandatory sm:snap-none sm:gap-6 sm:px-6">
            {filteredBoard.columns.map((column) => (
              <Column key={column.id} column={column} />
            ))}

            {canManageColumns && (
              <div className="flex-shrink-0 w-[85vw] snap-center sm:w-80">
                {!isColumnComposerOpen ? (
                  <button
                    onClick={openColumnComposer}
                    className="w-full rounded-2xl border border-dashed border-slate-300 bg-white/70 hover:bg-white px-5 py-5 text-left shadow-sm transition-all duration-200"
                  >
                    <div className="flex items-center gap-3 text-slate-800 font-semibold">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </span>
                      Add a new column
                    </div>
                    <p className="mt-3 text-sm text-slate-500">
                      Create custom stages for this board. Good for review, published, parked, or anything else your workflow needs.
                    </p>
                  </button>
                ) : (
                  <form
                    onSubmit={handleCreateColumn}
                    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-lg"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-base font-semibold text-slate-900">New column</h3>
                      <button
                        type="button"
                        onClick={() => {
                          setIsColumnComposerOpen(false);
                          setColumnError('');
                        }}
                        className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <label className="mt-4 block text-sm font-medium text-slate-700">
                      Column name
                    </label>
                    <input
                      type="text"
                      value={newColumnName}
                      onChange={(e) => setNewColumnName(e.target.value)}
                      placeholder="For example: Review"
                      autoFocus
                      className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                    />

                    <label className="mt-4 block text-sm font-medium text-slate-700">
                      Header color
                    </label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {COLUMN_COLOR_OPTIONS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewColumnColor(color)}
                          className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-105 ${
                            newColumnColor === color ? 'border-slate-700' : 'border-white'
                          }`}
                          style={{ backgroundColor: color }}
                          aria-label={`Select column color ${color}`}
                        />
                      ))}
                    </div>

                    {columnError && (
                      <p className="mt-3 text-sm text-red-600">{columnError}</p>
                    )}

                    <div className="mt-5 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsColumnComposerOpen(false);
                          setColumnError('');
                        }}
                        className="rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isCreatingColumn}
                        className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isCreatingColumn ? 'Adding...' : 'Add Column'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {filterTagId && totalCards === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center p-8 bg-white/85 backdrop-blur rounded-xl shadow-lg">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <p className="text-gray-500">No documents with this tag in this board.</p>
              </div>
            </div>
          )}

          <DragOverlay>
            {activeCard && (
              <div className="rotate-6 shadow-2xl scale-105">
                <Card card={activeCard} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
        <SwipeIndicator count={columnCount} activeIndex={activeColumnIndex} scrollRef={scrollContainerRef} />
      </div>
    </div>
  );
}
